import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { Box, useMediaQuery } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { ReactComponent as EmptyIcon } from 'Assets/img/empty.svg'
import { UserItem } from 'Components/_common/SearchUser/UserItem'
import SearchUserSkeleton from 'Components/ContestComponents/ContestCreate/SearchUserSkeleton'
import { NotificationContext } from 'Components/Notification/context'
import SearchField from 'Components/SearchComponents/SearchField'
import { debounce, map } from 'lodash-es'
import {
  useContestRoundsQuery,
  useDeleteJudgeMutation,
  useSaveJudgeMutation,
  useSortJudgeMutation,
} from 'Queries/Contests'
import { useSearchUsersQuery } from 'Queries/Users'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Else, If, Then, When } from 'react-if'

import FAIcon from '../../../../Theme/FAIcon'
import { FA_ICONS } from '../../../../Theme/font-awesome-icon-constants'
import { BREAKPOINT_MIN_SM } from '../../../../Theme/stylesUtil'
import CustomMuiTab from '../../../../Theme/Tab'
import { transformTextWithThreeDots } from '../../../../utils/text-transformation-utils'
import MainSwipableDrawer from '../../../Mobile/MainSwipableDrawer'
import { JudgeCard } from './JudgeCard'
import { JudgeControls } from './JudgeControls'
import { getAllRoundsValue } from './utils'

export const SearchJudgesContainer = ({
  roundLimits,
  styles,
  judges,
  selectedJudge,
  setSelectedJudge,
  search,
  setSearch,
  showEmpty = false,
  contestId,
  ownerId,
  action,
  isModal,
  setValue,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isOpenDrawer, setIsOpenDrawer] = useState(false)
  const { data: searchedUsers = [], isFetched, isFetching } = useSearchUsersQuery({ search })
  const { data: roundNames } = useContestRoundsQuery(roundLimits)

  const { mutate: saveJudge, isLoading: isSaveJudgeLoading } = useSaveJudgeMutation(contestId, !isModal)
  const { mutate: deleteJudge, isLoading: isDeleteJudgeLoading } = useDeleteJudgeMutation(contestId, !isModal)
  const { mutate: sortJudges } = useSortJudgeMutation(contestId)

  const [currentJudges, setJudges] = useState(judges)
  const { notification } = useContext(NotificationContext)

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor))

  const debouncedHandleSearch = useMemo(() => debounce(setSearch, 300), [setSearch])
  const isEmpty = !searchedUsers.length && !isFetching
  const judgeIds = map(judges, 'user_id')
  const isBiggerThanSMScreen = useMediaQuery(BREAKPOINT_MIN_SM)

  useEffect(() => () => setSearch(''), [setSearch])

  useEffect(() => {
    setJudges(judges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judges])

  useEffect(() => {
    if (action !== 'create' || !roundNames?.length) return

    const allRounds = getAllRoundsValue(roundNames)

    judges.forEach((judge, index) => {
      setValue(`judges[${index}].rounds`, allRounds)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(roundNames), action])

  const handleClick = (id) => {
    setSelectedJudge(id)
  }

  const handleChooseJudge = (id, index) => {
    setActiveTab(index)
    setSelectedJudge(id)
  }

  const handleOpenJudgesDrawer = () => {
    setIsOpenDrawer(true)
  }
  const handleSaveJudge = (userId) => {
    saveJudge(userId, {
      onSuccess: ({ data }) => {
        notification.success('Judge was added successfully')

        if (!setValue) return
        const newJudge = data.data[0]
        setValue('judges', [
          ...judges,
          {
            ...newJudge,
            ...data.relations?.contest_judges[newJudge.id],
            ...data.relations.users[newJudge.user_id],
          },
        ])
      },
      onError: ({ response }) => {
        notification.error(response.data.name)
      },
    })
  }

  const handleDeleteJudge = (judge) => {
    deleteJudge(judge, {
      onSuccess: () => {
        notification.success('Judge was deleted successfully')

        if (!setValue) return
        setValue(
          'judges',
          judges.filter(({ user_id }) => user_id !== judge.judgeId),
        )
      },
    })
  }

  const onSortEnd = ({ active, over }) => {
    const prevItems = [...judges]
    const oldIndex = prevItems.findIndex((item) => item.id === active?.id)
    const newIndex = prevItems.findIndex((item) => item.id === over?.id)

    const result = prevItems.slice()
    const item = result.splice(oldIndex, 1)
    const sortedJudges = []
      .concat(result.slice(0, newIndex))
      .concat(item)
      .concat(result.slice(newIndex, prevItems.length))

    setJudges(sortedJudges)
    setValue ? setValue('judges', sortedJudges) : sortJudges({ judges: sortedJudges })
  }

  const judgesToShow = isModal ? currentJudges : judges

  return (
    <Box
      sx={{
        minWidth: isModal || action !== 'edit' ? '100%' : '270px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxHeight: action !== 'edit' && isModal ? 'calc(100vh - 440px)' : 'initial',
      }}
    >
      <When condition={!isBiggerThanSMScreen}>
        <Tabs
          value={activeTab}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
        >
          {judgesToShow?.map((judge, index) => (
            <CustomMuiTab
              key={judge.id}
              value={index}
              onClick={() => handleChooseJudge(judge.id, index)}
              label={<Typography variant="h6">{transformTextWithThreeDots(judge.login)}</Typography>}
            />
          ))}
          <CustomMuiTab
            label={
              <FAIcon
                iconClassName={FA_ICONS.PLUS_CIRCLE}
                iconFontSize={18}
                color="text.tertiary"
              />
            }
            onClick={handleOpenJudgesDrawer}
          />
        </Tabs>

        <MainSwipableDrawer
          drawerHeight={'75vh'}
          setIsOpenDrawer={setIsOpenDrawer}
          isOpenDrawer={isOpenDrawer}
        >
          <Box sx={{ padding: '32px 16px' }}>
            <Typography
              variant="h5"
              color="text.primary"
              sx={{ marginBottom: '15px' }}
            >
              Add Judge
            </Typography>
            <SearchField
              placeholder="Enter name to Invite Judges"
              value={search}
              onChange={debouncedHandleSearch}
              allowClear
              isMobile
            />
            <When condition={!!search}>
              <If condition={!isFetched}>
                <Then>
                  <SearchUserSkeleton />
                </Then>
                <Else>
                  {searchedUsers?.map((user) => (
                    <UserItem
                      user={user}
                      key={user.id}
                      controls={
                        <JudgeControls
                          deleteJudge={handleDeleteJudge}
                          contestId={contestId}
                          saveJudge={handleSaveJudge}
                          userIds={judgeIds}
                          ownerId={ownerId}
                          isDeleteJudgeLoading={isDeleteJudgeLoading}
                          isSaveJudgeLoading={isSaveJudgeLoading}
                        />
                      }
                    />
                  ))}
                </Else>
              </If>
            </When>
          </Box>
        </MainSwipableDrawer>
      </When>
      <When condition={isBiggerThanSMScreen}>
        <SearchField
          placeholder="Enter name to Invite Judges"
          value={search}
          onChange={debouncedHandleSearch}
          allowClear
          isMobile
        />
      </When>

      <When condition={isBiggerThanSMScreen}>
        <Box sx={styles}>
          <If condition={!!search}>
            <Then>
              <If condition={!isFetched}>
                <Then>
                  <SearchUserSkeleton />
                </Then>
                <Else>
                  {searchedUsers?.map((user) => (
                    <UserItem
                      user={user}
                      key={user.id}
                      controls={
                        <JudgeControls
                          deleteJudge={handleDeleteJudge}
                          contestId={contestId}
                          saveJudge={handleSaveJudge}
                          userIds={judgeIds}
                          ownerId={ownerId}
                          isDeleteJudgeLoading={isDeleteJudgeLoading}
                          isSaveJudgeLoading={isSaveJudgeLoading}
                        />
                      }
                    />
                  ))}
                </Else>
              </If>
            </Then>
            <Else>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onSortEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={judgesToShow.map((item) => item.id)}>
                  {judgesToShow?.map((judge, index) => (
                    <JudgeCard
                      roundNames={roundNames}
                      judge={judge}
                      key={judge.id}
                      isSelected={selectedJudge === judge.id && !isModal}
                      onClick={handleClick}
                      setValue={setValue}
                      isModal={isModal}
                      sortId={judge.id}
                      index={index}
                      action={action}
                      contestId={contestId}
                      ownerId={ownerId}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Else>
          </If>

          {isEmpty && showEmpty && (
            <Box
              sx={(theme) => ({
                background: '#0000000D',
                borderRadius: theme.spacing(2),
                padding: theme.spacing(4, 2),
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              })}
            >
              <EmptyIcon />
            </Box>
          )}
        </Box>
      </When>
    </Box>
  )
}
